import Link from 'components/ui/AppLink';
import ContainerBox from 'components/ui/ContainerBox';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Breadcrumbs,
    Button,
    Grid,
    Tab,
    Tabs,
    Typography,
    Typography as BreadcrumbsText,
    useMediaQuery,
} from '@mui/material';
import { Checkbox } from 'components/ui/FormControls';
import React, { useState } from 'react';

import { baseUrls } from '../../../../utils/baseUrls.utils';
import { subscribersMainSx } from './ChannelSubscribersShared';
import AdminList from '../../../ui/admin/AdminList';
import StatCards from '../../../ui/StatCards';
import { ShortDashboardUser, ShortUser } from '../../../../shared/types';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { TextOverlapping } from '../../../ui/TextOverlapping';

type TABS = 'subscriptions' | 'noInsightsAccount' | 'unmatched' | 'existing';

enum TAB_TITLES {
    subscriptions = 'New subscribers',
    noInsightsAccount = 'No Insights account',
    existing = 'Existing subscribers',
    unmatched = 'No Neon account',
}

interface PremiumChannelSubscribersBulkAddProps {
    channel: {
        _id: string;
        name: string;
    };
    unmatched: string[];
    users: ShortUser[];
    neonUsers: ShortDashboardUser[];
    existing: ShortUser[];
    onCancel: () => void;
    onConfirm: (overwriteSubscribed: boolean) => void;
}

export const ChannelSubscribersBulkAdd = ({
    channel,
    unmatched,
    users,
    existing,
    neonUsers,
    onCancel,
    onConfirm,
}: PremiumChannelSubscribersBulkAddProps): JSX.Element => {
    const theme = useTheme();
    const total =
        users.length + existing.length + neonUsers.length + unmatched.length;
    const [overwriteSubscribed, setOverwriteSubscribed] = useState(false);

    const [tab, setTab] = useState<TABS>('subscriptions');

    const cards = [
        {
            label: 'Email addresses uploaded',
            value: total,
        },
        {
            label: TAB_TITLES.subscriptions,
            value: users.length,
        },
        {
            label: TAB_TITLES.existing,
            value: existing.length,
        },
        {
            label: TAB_TITLES.noInsightsAccount,
            value: neonUsers.length,
        },
        {
            label: TAB_TITLES.unmatched,
            value: unmatched.length,
        },
    ];

    const tabs = [
        {
            value: 'subscriptions',
            title: TAB_TITLES.subscriptions,
        },
        {
            value: 'existing',
            title: TAB_TITLES.existing,
        },
        {
            value: 'noInsightsAccount',
            title: TAB_TITLES.noInsightsAccount,
        },
        {
            value: 'unmatched',
            title: TAB_TITLES.unmatched,
        },
    ];

    const usersCols: ColDef[] = [
        {
            field: 'name',
            cellRenderer: (props: ICellRendererParams) => (
                <Link
                    target="_blank"
                    to={baseUrls.publisher.users.edit.replace(
                        ':_id',
                        props.data._id
                    )}
                >
                    <strong>{props.data.name}</strong>
                </Link>
            ),
        },
        {
            field: 'company',
        },
        {
            field: 'email',
        },
    ];

    const shortNeonUserId = useMediaQuery(theme.breakpoints.down(1600));

    const neonUsersCols: ColDef[] = [
        {
            field: 'name',
            valueFormatter: (p) =>
                [p.data.firstName, p.data.lastName]
                    .filter((f) => !!f)
                    .join(' '),
        },
        {
            field: 'company',
        },
        {
            field: 'auth0email',
            headerName: 'Email',
        },
        {
            field: 'neonUserId',
            width: 400,
            cellRenderer: (props: ICellRendererParams) =>
                shortNeonUserId ? (
                    <TextOverlapping
                        value={props.value}
                        maxLength={50}
                        maxWidth={350}
                    />
                ) : (
                    <>{props.value}</>
                ),
        },
    ];

    const canConfirm =
        users.length > 0 || (overwriteSubscribed && existing.length > 0);

    const buttons = (
        <Box
            sx={{
                display: 'flex',
            }}
        >
            <Box
                sx={{
                    mr: 2,
                }}
            >
                <Button
                    variant="contained"
                    onClick={() => onConfirm(overwriteSubscribed)}
                    disabled={!canConfirm}
                >
                    Confirm and add subscribers
                </Button>
            </Box>
            <Box>
                <Button variant="outlined" color="inherit" onClick={onCancel}>
                    Cancel
                </Button>
            </Box>
        </Box>
    );

    return (
        <ContainerBox>
            <Box sx={subscribersMainSx}>
                <Box sx={{ mt: 2 }}>
                    <Breadcrumbs>
                        <Link to={baseUrls.publisher.premiumChannels.list}>
                            Channels
                        </Link>
                        <Link
                            to={baseUrls.publisher.premiumChannels.subscribersStats.replace(
                                ':_id',
                                channel._id || ''
                            )}
                        >
                            {channel.name}
                        </Link>
                        <BreadcrumbsText>Add subscribers</BreadcrumbsText>
                    </Breadcrumbs>
                </Box>
                <Grid container spacing={4}>
                    <Grid size={12}>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h5">Review upload</Typography>
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Typography
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                A total of <b>{total} email addresses</b> were
                                identified in the CSV file, of which{' '}
                                {users.length} can be added as new subscribers
                                to the channel.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={12}>
                        <StatCards items={cards} />
                    </Grid>
                    <Grid size={12}>{buttons}</Grid>
                    <Grid size={12}>
                        <Tabs
                            value={tab}
                            onChange={(_, value) => setTab(value as TABS)}
                        >
                            {tabs.map((item) => (
                                <Tab
                                    key={item.value}
                                    value={item.value}
                                    label={item.title}
                                />
                            ))}
                        </Tabs>
                    </Grid>
                    {tab === 'subscriptions' && (
                        <Grid size={12}>
                            <Typography variant="h6">
                                Insights users to be added as subscribers:{' '}
                                {users.length}
                            </Typography>
                            <AdminList
                                items={users}
                                showAdd={false}
                                loading={false}
                                showCaption={false}
                                showHeader={false}
                                columns={usersCols}
                                removeBottomOffset
                            />
                        </Grid>
                    )}
                    {tab === 'existing' && (
                        <Grid size={12}>
                            <Typography variant="h6">
                                Insights users already subscribed to this
                                channel: {existing.length}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Checkbox
                                    label="Overwrite subscribed users"
                                    value="overwriteSubscribed"
                                    checked={overwriteSubscribed}
                                    onChange={({ target }) =>
                                        setOverwriteSubscribed(target.checked)
                                    }
                                />
                            </Box>
                            <AdminList
                                items={existing}
                                showAdd={false}
                                loading={false}
                                showCaption={false}
                                showHeader={false}
                                columns={usersCols}
                                removeBottomOffset
                            />
                        </Grid>
                    )}
                    {tab === 'noInsightsAccount' && (
                        <Grid size={12}>
                            <Typography variant="h6">
                                Neon users without Insights account:{' '}
                                {neonUsers.length}
                            </Typography>
                            <AdminList
                                items={neonUsers}
                                showAdd={false}
                                loading={false}
                                showCaption={false}
                                showHeader={false}
                                columns={neonUsersCols}
                                removeBottomOffset
                            />
                        </Grid>
                    )}
                    {tab === 'unmatched' && (
                        <Grid size={12}>
                            <Typography variant="h6">
                                Email addresses that didn’t match with Neon
                                identities: {unmatched.length}
                            </Typography>
                            <AdminList
                                items={unmatched
                                    .sort()
                                    .map((email) => ({ email }))}
                                showAdd={false}
                                loading={false}
                                showCaption={false}
                                showHeader={false}
                                columns={[{ field: 'email' }]}
                                removeBottomOffset
                            />
                        </Grid>
                    )}
                    <Grid size={12}>{buttons}</Grid>
                </Grid>
            </Box>
        </ContainerBox>
    );
};
